import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import PersonIcon from "@mui/icons-material/Person";
import MapIcon from "@mui/icons-material/Map";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import QrCodeIcon from "@mui/icons-material/QrCode";
import axios from "axios";

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function Members() {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchUsername, setSearchUsername] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [qrValue, setQrValue] = useState("");
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const settingsRes = await axios.get("/api/users/settings", {
          headers: authHeader(),
        });
        // Lokale Variable verwenden – kein Race-Condition-Problem mit State
        const adminStatus = settingsRes.data?.isAdmin === true;
        setIsCurrentUserAdmin(adminStatus);

        const apiUrl =
          adminStatus && showAllUsers
            ? "/api/admin/all-users"
            : "/api/global/public-members";

        const membersRes = await axios.get(apiUrl, { headers: authHeader() });
        const withId = membersRes.data.map((user, index) => ({
          id: user.user_id || index,
          ...user,
        }));
        setMembers(withId);
        setFilteredMembers(withId);
      } catch (error) {
        console.error("Fehler beim Laden der Mitglieder:", error);
        if (error.response?.status === 401) navigate("/login");
      }
    };
    fetchMembers();
  }, [showAllUsers, navigate]);

  useEffect(() => {
    const filtered = members.filter((user) =>
      user.username.toLowerCase().includes(searchUsername.toLowerCase())
    );
    setFilteredMembers(filtered);
  }, [searchUsername, members]);

  const handleQrOpen = (event, trainercode) => {
    if (!trainercode) return;
    setQrValue(trainercode);
    setAnchorEl(event.currentTarget);
  };

  const columns = [
    {
      field: "username",
      headerName: "Nutzername",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {params.value}
        </Box>
      ),
    },
    ...(isCurrentUserAdmin
      ? [
          {
            field: "public_profile",
            headerName: "Öffentlich",
            width: 90,
            renderCell: (params) => (
              <Typography variant="body2">
                {params.value ? "Ja" : "Nein"}
              </Typography>
            ),
          },
        ]
      : []),
    {
      field: "actions",
      headerName: "Aktionen",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title="QR-Code">
            <span>
              <IconButton
                size="small"
                onClick={(e) => handleQrOpen(e, params.row.trainercode)}
                disabled={!params.row.trainercode}
              >
                <QrCodeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Profil">
            <IconButton
              size="small"
              onClick={() => navigate(`/profile/${params.row.id}`)}
            >
              <PersonIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Arenen">
            <IconButton
              size="small"
              onClick={() =>
                navigate(`/Extern/ArenamapExtern/${params.row.id}`)
              }
            >
              <MapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Medaillen">
            <IconButton
              size="small"
              onClick={() => navigate(`/ProfileMedals/${params.row.id}`)}
            >
              <MilitaryTechIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: 2,
        pb: { xs: "88px", sm: "72px" },
        maxWidth: 800,
        mx: "auto",
      }}
    >
      <Typography variant="h5" gutterBottom>
        Mitgliederliste
      </Typography>

      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        mb={2}
        alignItems={{ sm: "center" }}
      >
        <TextField
          label="Suche nach Nutzername"
          variant="outlined"
          size="small"
          fullWidth
          value={searchUsername}
          onChange={(e) => setSearchUsername(e.target.value)}
        />
        {isCurrentUserAdmin && (
          <FormControlLabel
            control={
              <Switch
                checked={showAllUsers}
                onChange={() => setShowAllUsers((prev) => !prev)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" noWrap>
                Alle Nutzer (Admin)
              </Typography>
            }
            sx={{ flexShrink: 0 }}
          />
        )}
      </Box>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
        <DataGrid
          rows={filteredMembers}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          autoHeight
          rowHeight={56}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              borderRadius: 0,
            },
          }}
        />
      </Paper>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setQrValue("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box p={2}>
          {qrValue && <QRCode value={qrValue} size={180} />}
        </Box>
      </Popover>
    </Box>
  );
}
